import { useLayoutEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  WORKBENCH_HANDOFF_QUERY,
  consumeWorkbenchNavHandoff,
  stashHandoffStudioPrefillBackups,
} from '@/pages/ipe/workbench/utils/workbenchNavHandoff';

/**
 * 新标签打开工作台时：消费 ?ipeHandoff= 并还原为 location.state，再清掉 query。
 *
 * 重要：工作台页面是 lazy 的，本组件会在 ImageProcessStudio / ModelStudio 等挂载之前就 consume handoff。
 * 因此必须在 consume 当下写入 sessionStorage 预填备份（含完整 payload：模特 slots、主图参数、AI 文案等），
 * 否则懒加载首屏读不到图/参数。
 */
export default function WorkbenchNavHandoffBootstrap() {
  const location = useLocation();
  const navigate = useNavigate();
  const lastTokenRef = useRef('');

  useLayoutEffect(() => {
    const sp = new URLSearchParams(location.search || '');
    const token = String(sp.get(WORKBENCH_HANDOFF_QUERY) || '').trim();
    if (!token || lastTokenRef.current === token) return;
    lastTokenRef.current = token;

    const handoffState = consumeWorkbenchNavHandoff(token);
    sp.delete(WORKBENCH_HANDOFF_QUERY);
    const qs = sp.toString();
    const next = `${location.pathname}${qs ? `?${qs}` : ''}${location.hash || ''}`;

    // 无论是否取到 payload，都清掉 query，避免地址栏残留
    if (!handoffState || typeof handoffState !== 'object') {
      navigate(next, {
        replace: true,
        state: location.state && typeof location.state === 'object' ? location.state : null,
      });
      return;
    }

    // 先写本标签备份，再 navigate；懒加载工作室挂载时 peek 已空，靠 backup 恢复
    stashHandoffStudioPrefillBackups(handoffState);

    navigate(next, {
      replace: true,
      state: {
        ...(location.state && typeof location.state === 'object' ? location.state : {}),
        ...handoffState,
      },
    });
  }, [location.hash, location.pathname, location.search, location.state, navigate]);

  return null;
}
